import { detectOverflow, flip, Middleware } from "@floating-ui/react";
import findLastIndex from "lodash/findLastIndex";
import first from "lodash/first";
import isNil from "lodash/isNil";
import last from "lodash/last";
import sum from "lodash/sum";

import { Day } from "../../booking_module_calendar/booking_calendar/booking_calendar_store";
import { findDayIndex, isDateBetween } from "../calendar.utils";

export const OVERBOOKINGS_HEIGHT = 6;

const FLIP_MARGIN = 20;
const SHIFT_MARGIN = 8;

const isBarOverlappingWholeCalendar = (start: string, end: string, days: Day[]) => {
  const firstCalendarDay = first(days);
  const lastCalendarDay = last(days);
  const startDate = new Date(start);
  const endDate = new Date(end);
  return (
    firstCalendarDay &&
    lastCalendarDay &&
    isDateBetween(firstCalendarDay.date, startDate, endDate) &&
    isDateBetween(lastCalendarDay.date, startDate, endDate)
  );
};

export const findFirstAndLastVisibleDaysIndices = (
  days: { hidden: boolean }[]
): { firstVisibleDayIndex: number | null; lastVisibleDayIndex: number | null } => {
  const firstVisibleDayIndex = days.findIndex(({ hidden }) => !hidden);
  if (firstVisibleDayIndex < 0) {
    return { firstVisibleDayIndex: null, lastVisibleDayIndex: null };
  }
  const lastVisibleDayIndex = findLastIndex(days, ({ hidden }) => !hidden);
  return { firstVisibleDayIndex, lastVisibleDayIndex };
};

export const calculateBarDimensions = (
  start: string,
  end: string,
  days: Day[]
): { left: number | null; width: number | null } => {
  const startDayIndex = findDayIndex(days, start);
  const endDayIndex = findDayIndex(days, end);

  if (isNil(startDayIndex) && isNil(endDayIndex)) {
    const isOverlappingCalendar = isBarOverlappingWholeCalendar(start, end, days);
    const { left: lastLeft = 0, width: lastWidth = 0 } = last(days) ?? {};
    return isOverlappingCalendar ? { left: 0, width: lastLeft + lastWidth } : { left: null, width: null };
  }
  const barDays = days.slice(startDayIndex ?? 0, (endDayIndex ?? days.length - 1) + 1);

  const { firstVisibleDayIndex, lastVisibleDayIndex } = findFirstAndLastVisibleDaysIndices(barDays);

  if (isNil(firstVisibleDayIndex) || isNil(lastVisibleDayIndex)) {
    return { left: null, width: null };
  }

  const { left: startLeft } = barDays[firstVisibleDayIndex];
  const visibleBarDays = barDays.slice(firstVisibleDayIndex, lastVisibleDayIndex + 1);
  const width = sum(visibleBarDays.map(({ width }) => width));

  return {
    left: startLeft,
    width
  };
};

export const flipOnlyWhenFullyOutOfBounds = (boundaryElement: HTMLDivElement | null): Middleware => ({
  name: "flipOnlyWhenFullyOutOfBounds",
  async fn(middlewareArguments) {
    const { placement, rects } = middlewareArguments;

    if (placement !== "top") {
      return {};
    }

    const overflow = await detectOverflow(middlewareArguments, {
      boundary: boundaryElement ?? undefined,
      padding: 8
    });
    const isReferenceOutsideLeft =
      overflow.left + FLIP_MARGIN > rects.reference.width / 2 + rects.floating.width / 2;
    const isReferenceOutsideRight =
      overflow.right + FLIP_MARGIN > rects.reference.width / 2 + rects.floating.width / 2;
    const isReferenceOutsideTop =
      overflow.top + FLIP_MARGIN > rects.reference.height / 2 + rects.floating.height / 2;
    const isReferenceOutside = isReferenceOutsideLeft || isReferenceOutsideTop || isReferenceOutsideRight;

    if (!isReferenceOutside) {
      return {
        reset: {
          placement: "top"
        }
      };
    }
    if (isReferenceOutsideLeft) {
      return {
        reset: {
          placement: "right"
        }
      };
    } else if (isReferenceOutsideRight) {
      return {
        reset: {
          placement: "left"
        }
      };
    } else if (isReferenceOutsideTop) {
      return {
        reset: {
          placement: "bottom"
        }
      };
    } else {
      return flip({
        boundary: boundaryElement ?? undefined,
        fallbackPlacements: ["right", "left", "bottom"]
      }).fn(middlewareArguments);
    }
  }
});

export const shiftInsideBoundary = (boundaryElement: HTMLElement | null): Middleware => ({
  name: "shiftInsideBoundary",
  fn: ({ x, y, rects }) => {
    if (!boundaryElement) {
      return {};
    }

    const boundaryRect = boundaryElement?.getBoundingClientRect();
    const { width: tooltipWidth, height: tooltipHeight } = rects.floating;

    const minX = boundaryRect.left + SHIFT_MARGIN;
    const maxX = boundaryRect.right - SHIFT_MARGIN - tooltipWidth;

    const minY = boundaryRect.top + SHIFT_MARGIN;
    const maxY = boundaryRect.bottom - SHIFT_MARGIN - tooltipHeight;

    return {
      x: Math.min(Math.max(x, minX), maxX),
      y: Math.min(Math.max(y, minY), maxY)
    };
  }
});
